/**
 * 信令连接逻辑 Hook 模块。
 */
import { useCallback, useState } from "react";
import { checkDevice, joinRoom, leaveRoom } from "@/services/remote/signaling";
import { useConnectionStore } from "@/stores/remote";
import { parseDeviceIdAndAccessCodeFromLink } from "@/utils/remote";
import {
	cacheConnection,
	clearSession,
	loadCachedConnection,
	persistSession,
} from "./connection-storage";

export const CONNECTION_STATUS = {
	IDLE: "idle",
	CONNECTING: "connecting",
	CONNECTED: "connected",
	ERROR: "error",
} as const;

type ConnectionStatus =
	(typeof CONNECTION_STATUS)[keyof typeof CONNECTION_STATUS];

type ConnectionSummary = {
	deviceId: string;
	passCode: string;
	deviceInfo?: Record<string, unknown> | null;
	sessionInfo?: Record<string, unknown> | null;
	expiresAt?: string;
};

type ConnectResult = {
	ok: boolean;
	message?: string;
};

/**
 * 封装信令连接流程，提供状态与操作给页面使用
 */
const useSignalConnection = () => {
	const { currentRoomId, setCurrentRoomId, resetRoom } = useConnectionStore(
		(state) => ({
			currentRoomId: state.currentRoomId,
			setCurrentRoomId: state.setCurrentRoomId,
			resetRoom: state.resetRoom,
		}),
	);
	const cachedDefaults = loadCachedConnection();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
		CONNECTION_STATUS.IDLE,
	);
	const [connectionSummary, setConnectionSummary] =
		useState<ConnectionSummary | null>(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [autoConnectTriggered, setAutoConnectTriggered] = useState(false);
	const [formDefaults] = useState({
		deviceId: cachedDefaults.deviceId || "",
		passCode: cachedDefaults.passCode || "",
	});
	const [linkPrefill, setLinkPrefill] = useState(cachedDefaults.link || "");

	/**
	 * 使用设备 ID 和访问码与信令服务建立连接
	 * @param {string} deviceId
	 * @param {string} passCode
	 */
	const connectUsingCredentials = useCallback(
		async (deviceId: string, passCode: string): Promise<ConnectResult> => {
			if (!deviceId || !passCode) {
				const message = "Device ID and access code are required";
				setErrorMessage(message);
				setConnectionStatus(CONNECTION_STATUS.ERROR);
				return { ok: false, message };
			}

			if (connectionStatus === CONNECTION_STATUS.CONNECTING) {
				return { ok: false, message: "Connection is in progress" };
			}

			setConnectionStatus(CONNECTION_STATUS.CONNECTING);
			setIsSubmitting(true);
			setErrorMessage("");
			cacheConnection(deviceId, passCode);

			try {
				if (currentRoomId) {
					leaveRoom();
				} else {
					resetRoom();
				}

				const result = await checkDevice(deviceId, passCode);

				await joinRoom(result.roomId);
				setCurrentRoomId(result.roomId);
				persistSession(result, deviceId, passCode);

				setConnectionSummary({
					deviceId,
					passCode,
					deviceInfo: result.deviceInfo,
					sessionInfo: result.sessionInfo,
					expiresAt: result.expiresAt,
				});

				setConnectionStatus(CONNECTION_STATUS.CONNECTED);
				return { ok: true };
			} catch (error) {
				console.error("connectDevice error:", error);
				setConnectionStatus(CONNECTION_STATUS.ERROR);
				const message =
					error instanceof Error
						? error.message
						: "Failed to connect to device";
				setErrorMessage(message);
				return { ok: false, message };
			} finally {
				setIsSubmitting(false);
			}
		},
		[connectionStatus, currentRoomId, resetRoom, setCurrentRoomId],
	);

	/**
	 * 断开当前连接并恢复初始状态
	 */
	const cancelConnection = useCallback(() => {
		leaveRoom();
		resetRoom();
		setConnectionSummary(null);
		setConnectionStatus(CONNECTION_STATUS.IDLE);
		clearSession();
	}, [resetRoom]);

	/**
	 * 如果链接自带邀请信息，则尝试自动连接
	 * @param {string} link
	 */
	const tryAutoConnectFromLink = useCallback(
		(link: string) => {
			if (!link) return false;

			if (
				autoConnectTriggered ||
				connectionStatus === CONNECTION_STATUS.CONNECTED ||
				connectionStatus === CONNECTION_STATUS.CONNECTING
			) {
				return false;
			}

			try {
				const parsed = parseDeviceIdAndAccessCodeFromLink(link) as {
					d: string;
					a: string;
				};
				setLinkPrefill(link);
				setAutoConnectTriggered(true);
				void connectUsingCredentials(parsed.d, parsed.a);
				return true;
			} catch (error) {
				const message = error instanceof Error ? error.message : "invalid link";
				console.log("URL auto connect skipped:", message);
				return false;
			}
		},
		[autoConnectTriggered, connectionStatus, connectUsingCredentials],
	);

	const shouldShowInvitation =
		connectionStatus === CONNECTION_STATUS.CONNECTED && currentRoomId;
	const shouldShowLoading =
		connectionStatus === CONNECTION_STATUS.CONNECTING || isSubmitting;
	const isError = connectionStatus === CONNECTION_STATUS.ERROR;

	return {
		connectionSummary,
		currentRoomId,
		shouldShowInvitation,
		shouldShowLoading,
		isError,
		errorMessage,
		submitCredentials: connectUsingCredentials,
		cancelConnection,
		tryAutoConnectFromLink,
		formDefaults,
		linkPrefill,
	};
};

export default useSignalConnection;
